import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ConditionalBlockView from './ConditionalBlockView';
import { DEFAULT_CONDITIONAL_ATTRS, RowenaConditionalAttrs } from './rowenaConditional';
import { RowenaAvailableVariable } from 'types/api/SystemTenant/RowenaAI/RowenaContentTypes';

export interface RowenaConditionalStorage {
  variables?: Record<string, RowenaAvailableVariable>;
  onEdit?: (pos: number, current: RowenaConditionalAttrs) => void;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    rowenaConditional: {
      insertRowenaConditional: (attrs?: Partial<RowenaConditionalAttrs>) => ReturnType;
      updateRowenaConditional: (pos: number, attrs: Partial<RowenaConditionalAttrs>) => ReturnType;
      unwrapRowenaConditional: (pos: number) => ReturnType;
    };
  }
}

export const ConditionalBlock = Node.create<unknown, RowenaConditionalStorage>({
  name: 'rowenaConditional',

  group: 'block',

  content: 'block+',

  defining: true,

  addStorage() {
    return {
      variables: undefined,
      onEdit: undefined,
    };
  },

  addAttributes() {
    return {
      variable: {
        default: DEFAULT_CONDITIONAL_ATTRS.variable,
        parseHTML: (el) => el.getAttribute('data-variable') ?? '',
        renderHTML: (attrs) => ({ 'data-variable': attrs.variable ?? '' }),
      },
      option: {
        default: DEFAULT_CONDITIONAL_ATTRS.option,
        parseHTML: (el) => el.getAttribute('data-option'),
        renderHTML: (attrs) => (attrs.option != null && attrs.option !== '' ? { 'data-option': attrs.option } : {}),
      },
      operator: {
        default: DEFAULT_CONDITIONAL_ATTRS.operator,
        parseHTML: (el) => el.getAttribute('data-operator') ?? DEFAULT_CONDITIONAL_ATTRS.operator,
        renderHTML: (attrs) => ({ 'data-operator': attrs.operator ?? DEFAULT_CONDITIONAL_ATTRS.operator }),
      },
      value: {
        default: DEFAULT_CONDITIONAL_ATTRS.value,
        parseHTML: (el) => el.getAttribute('data-value') ?? '',
        renderHTML: (attrs) => ({ 'data-value': attrs.value ?? '' }),
      },
      valueKind: {
        default: DEFAULT_CONDITIONAL_ATTRS.valueKind,
        parseHTML: (el) => el.getAttribute('data-value-kind') ?? DEFAULT_CONDITIONAL_ATTRS.valueKind,
        renderHTML: (attrs) => ({ 'data-value-kind': attrs.valueKind ?? DEFAULT_CONDITIONAL_ATTRS.valueKind }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="rowena-conditional"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes({ 'data-type': 'rowena-conditional', class: 'rowena-conditional' }, HTMLAttributes),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ConditionalBlockView);
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { editor } = this;
        const { $from, empty } = editor.state.selection;
        if (!empty) return false;

        // Find the nearest enclosing rowenaConditional.
        let condDepth = -1;
        for (let d = $from.depth - 1; d >= 0; d--) {
          if ($from.node(d).type.name === this.name) {
            condDepth = d;
            break;
          }
        }
        if (condDepth < 0) return false;

        // Only react when cursor is in an empty paragraph that is the last
        // child of the conditional — same exit pattern as blockquote/list-item.
        const paraDepth = condDepth + 1;
        if ($from.depth < paraDepth) return false;
        const para = $from.node(paraDepth);
        if (!para || para.content.size > 0) return false;
        const cond = $from.node(condDepth);
        if ($from.index(condDepth) !== cond.childCount - 1) return false;

        const condEndPos = $from.after(condDepth);

        if (cond.childCount === 1) {
          // Only child — can't delete (would empty the block); just create
          // a paragraph after the conditional and move there.
          return editor
            .chain()
            .insertContentAt(condEndPos, { type: 'paragraph' })
            .focus(condEndPos + 1)
            .run();
        }

        const paraStart = $from.before(paraDepth);
        const paraEnd = $from.after(paraDepth);
        const insertAt = condEndPos - (paraEnd - paraStart);

        return editor
          .chain()
          .deleteRange({ from: paraStart, to: paraEnd })
          .insertContentAt(insertAt, { type: 'paragraph' })
          .focus(insertAt + 1)
          .run();
      },
    };
  },

  addCommands() {
    return {
      insertRowenaConditional:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent([
            {
              type: this.name,
              attrs: { ...DEFAULT_CONDITIONAL_ATTRS, ...attrs },
              content: [{ type: 'paragraph' }],
            },
            { type: 'paragraph' },
          ]),
      updateRowenaConditional:
        (pos, attrs) =>
        ({ tr, dispatch }) => {
          const node = tr.doc.nodeAt(pos);
          if (!node || node.type.name !== this.name) return false;
          if (dispatch) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs });
          }
          return true;
        },
      unwrapRowenaConditional:
        (pos) =>
        ({ tr, dispatch }) => {
          const node = tr.doc.nodeAt(pos);
          if (!node || node.type.name !== this.name) return false;
          if (dispatch) {
            tr.replaceWith(pos, pos + node.nodeSize, node.content);
          }
          return true;
        },
    };
  },
});
